import React from "react";
import { Button, Pagination, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import Markdown from "markdown-to-jsx";
import UserAvatar from "../UserAvatar";
import moment from "moment-jalaali";

import EmptyState from "../EmptyState";
import Translator from "./translate.json";
import styles from "./styles.module.css";

moment.loadPersian({ dialect: "persian-modern" });

const ACTIVITY_TONE = {
  status_changed: "orange",
  created: "green",
  updated: "blue",
  deleted: "red",
};

/* Activity `action` values arrive as absolute URLs; routing with the full URL
 * would trigger a page reload, so strip it down to an in-app path. */
const toInternalPath = (action) => {
  try {
    const url = new URL(action, window.location.origin);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return action;
  }
};

const relativeStamp = (iso) => {
  const at = moment(iso);
  const time = at.format("HH:mm");
  const today = moment().format("jYYYY/jMM/jDD");
  const day = at.format("jYYYY/jMM/jDD");
  const yesterday = moment().subtract(1, "day").format("jYYYY/jMM/jDD");

  if (day === today) return `امروز ${time}`;
  if (day === yesterday) return `دیروز ${time}`;
  return `${at.format("jYYYY/jMM/jDD")} • ${time}`;
};

const ActivityRow = ({ row }) => {
  const navigate = useNavigate();
  const causer =
    `${row.causer?.first_name || ""} ${row.causer?.last_name || ""}`.trim();
  const tone = ACTIVITY_TONE[row.log_name] || "blue";

  return (
    <div className={styles.row}>
      <UserAvatar user={row.causer} size={30} className={styles.avatar} />

      <div className={styles.body}>
        <div className={styles.text}>
          <Markdown
            options={{
              createElement: (type, props, children) =>
                React.createElement(
                  type,
                  { ...props, key: props?.key },
                  children,
                ),
            }}
          >
            {row?.translated_description || ""}
          </Markdown>
        </div>
        <div className={styles.meta}>
          <span className={`${styles.tag} ${styles[`tag_${tone}`]}`}>
            {Translator[row.log_name] || row.log_name}
          </span>
          {causer && <span className={styles.metaText}>{causer}</span>}
          <span className={styles.metaDot} />
          <span className={styles.metaText}>
            {relativeStamp(row.created_at)}
          </span>
        </div>
      </div>

      {row?.action && (
        <div className={styles.actions}>
          <Tooltip title="رفتن به مورد">
            <Button
              type="text"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(toInternalPath(row.action))}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

/**
 * Shared activity history panel used by both the single-project history tab and
 * the organization-wide history report, so the two stay visually identical.
 */
const ActivityFeed = ({
  rows = [],
  total = 0,
  page = 1,
  perPage = 8,
  onPageChange,
  isFetching = false,
  isFiltering = false,
  toolbar,
  title,
  hint,
  emptyDescription = "در این بازه موردی در تاریخچه ثبت نشده است.",
  filteredDescription = "با این فیلترها موردی در تاریخچه پیدا نشد. فیلترها را بازتر کن.",
}) => (
  <div className={`ds-panel ${styles.panel}`}>
    {(title || toolbar) && (
      <div className={styles.panelHeader}>
        {title && (
          <div className="section-heading">
            <div className="section-heading-icon kpi-icon-purple">
              <HistoryOutlined />
            </div>
            <h6 className="section-heading-text">{title}</h6>
            {hint && (
              <Tooltip title={hint} trigger={["hover", "click"]}>
                <InfoCircleOutlined className={styles.hint} />
              </Tooltip>
            )}
          </div>
        )}
        {toolbar && <div className={styles.toolbar}>{toolbar}</div>}
      </div>
    )}

    <div className={styles.panelBody}>
      {rows.length > 0 ? (
        <>
          <div className={isFetching ? styles.listLoading : undefined}>
            {rows.map((row) => (
              <ActivityRow key={row.id} row={row} />
            ))}
          </div>
          {/* No handler means no paging: the dashboard card is a glance and
              hands off to the history report from its header instead of
              browsing hundreds of rows six at a time. The report itself passes
              one and still pages. */}
          {onPageChange && total > perPage && (
            <Pagination
              className={styles.pagination}
              size="small"
              showSizeChanger={false}
              current={page}
              pageSize={perPage}
              total={total}
              onChange={onPageChange}
            />
          )}
        </>
      ) : (
        <div className={styles.empty}>
          <EmptyState
            compact
            icon={<HistoryOutlined />}
            filtered={isFiltering}
            filteredDescription={filteredDescription}
            title="هنوز تاریخچه‌ای ثبت نشده"
            description={emptyDescription}
          />
        </div>
      )}
    </div>
  </div>
);

export default ActivityFeed;
